import Papa from 'papaparse';
import React, { useMemo, useState } from 'react';
import { Modal } from '../../components/ui/modal';
import {
  api,
  apiEndpoints,
  type ApiRequestOptions,
} from '../../utils/apiService';
import { normalizeImportPhone } from '../../utils/vipPhone';

const MAX_ROWS = 2000;

type Step = 'upload' | 'map' | 'validate' | 'report';
type ImportMode = 'replace' | 'merge';

type VipFieldKey = 'phone' | 'first_name' | 'last_name' | 'full_name';

const TARGET_FIELDS: Array<{
  key: VipFieldKey;
  label: string;
  required?: boolean;
  aliases: string[];
}> = [
  {
    key: 'phone',
    label: 'Phone',
    required: true,
    aliases: ['phone', 'phone_number', 'mobile', 'cell', 'telephone'],
  },
  {
    key: 'first_name',
    label: 'First name',
    aliases: ['first_name', 'first', 'firstname', 'fname'],
  },
  {
    key: 'last_name',
    label: 'Last name',
    aliases: ['last_name', 'last', 'lastname', 'lname', 'surname'],
  },
  {
    key: 'full_name',
    label: 'Full name',
    aliases: ['full_name', 'name', 'vip_name', 'contact_name'],
  },
];

const TEMPLATE_CSV = [
  'first_name,last_name,full_name,phone',
  'Ada,Lovelace,,(555) 123-4567',
  ',,Grace Hopper,+15559876543',
].join('\n');

interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
  fileName: string;
}

interface VipPayload {
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
}

interface ValidatedRow {
  index: number;
  payload: VipPayload;
  status: 'ok' | 'skipped_duplicate' | 'invalid';
  reason?: string;
}

interface ImportReport {
  created: number;
  updated: number;
  skipped: Array<{ row: number; reason?: string; value?: string }>;
  invalid: Array<{ row: number; errors?: Record<string, unknown> }>;
}

interface VipCsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
  scope: 'store' | 'group';
  storeId: number | '';
  storeGroupId: number | '';
  requestOptions: ApiRequestOptions;
}

const autoMapColumns = (headers: string[]): Record<string, string> => {
  const mapping: Record<string, string> = {};
  const normalizedHeaders = headers.map((h) => ({
    original: h,
    normalized: h
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_'),
  }));
  for (const field of TARGET_FIELDS) {
    const match = normalizedHeaders.find((h) =>
      field.aliases.includes(h.normalized),
    );
    if (match) {
      mapping[field.key] = match.original;
    }
  }
  return mapping;
};

const VipCsvImportModal: React.FC<VipCsvImportModalProps> = ({
  isOpen,
  onClose,
  onImported,
  scope,
  storeGroupId,
  requestOptions,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [mode, setMode] = useState<ImportMode>('replace');
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const reset = () => {
    setStep('upload');
    setMode('replace');
    setConfirmReplace(false);
    setParsed(null);
    setMapping({});
    setUploadError(null);
    setReport(null);
    setImportError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (file: File | undefined) => {
    setUploadError(null);
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadError(
        'Please choose a .csv file. If this is an Excel workbook, export the sheet as CSV first.',
      );
      return;
    }
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      complete: (result) => {
        const headers = (result.meta.fields ?? []).filter(Boolean);
        const rows = result.data;
        if (headers.length === 0 || rows.length === 0) {
          setUploadError('This file appears to be empty or has no header row.');
          return;
        }
        if (rows.length > MAX_ROWS) {
          setUploadError(
            `This file has ${rows.length.toLocaleString()} rows; the limit is ${MAX_ROWS.toLocaleString()} per import.`,
          );
          return;
        }
        setParsed({ headers, rows, fileName: file.name });
        setMapping(autoMapColumns(headers));
        setStep('map');
      },
      error: (err) =>
        setUploadError(`Could not parse the file: ${err.message}`),
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'vip_contacts_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const sampleValue = (header: string): string => {
    if (!parsed) return '';
    for (const row of parsed.rows.slice(0, 5)) {
      const value = (row[header] ?? '').trim();
      if (value) return value;
    }
    return '';
  };

  const validatedRows: ValidatedRow[] = useMemo(() => {
    if (!parsed) return [];
    const seenPhones = new Set<string>();
    return parsed.rows.map((row, index) => {
      const payload: VipPayload = {
        first_name: '',
        last_name: '',
        full_name: '',
        phone: '',
      };
      for (const field of TARGET_FIELDS) {
        const header = mapping[field.key];
        if (header) {
          const value = (row[header] ?? '').trim();
          if (value) payload[field.key] = value;
        }
      }
      if (!payload.phone) {
        return {
          index,
          payload,
          status: 'invalid' as const,
          reason: 'Missing phone',
        };
      }
      const normalized = normalizeImportPhone(payload.phone);
      if (!normalized) {
        return {
          index,
          payload,
          status: 'invalid' as const,
          reason:
            'Phone must be a US/Canada number (10 digits, or 11 starting with 1)',
        };
      }
      payload.phone = normalized;
      if (seenPhones.has(normalized)) {
        return {
          index,
          payload,
          status: 'skipped_duplicate' as const,
          reason: `Duplicate phone in file (${normalized})`,
        };
      }
      seenPhones.add(normalized);
      return { index, payload, status: 'ok' as const };
    });
  }, [parsed, mapping]);

  const okRows = validatedRows.filter((r) => r.status === 'ok');
  const skippedRows = validatedRows.filter(
    (r) => r.status === 'skipped_duplicate',
  );
  const invalidRows = validatedRows.filter((r) => r.status === 'invalid');
  const phoneIsMapped = Boolean(mapping.phone);

  const handleImport = async () => {
    if (mode === 'replace' && !confirmReplace) {
      setImportError('Confirm that you want to replace the entire VIP list.');
      return;
    }
    setImportError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        scope,
        mode,
        contacts: okRows.map((r) => r.payload),
      };
      if (scope === 'group') {
        body.store_group_id = storeGroupId;
      }
      const response = await api.post(
        apiEndpoints.importVipContacts(),
        body,
        requestOptions,
      );
      if (!response) {
        setImportError('Import failed: no response from server.');
        return;
      }
      const payload = await response.json();
      if (!response.ok) {
        setImportError(
          payload.detail || `Import failed (status ${response.status}).`,
        );
        return;
      }
      setReport(payload as ImportReport);
      setStep('report');
      onImported();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Unexpected error.');
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = ['upload', 'map', 'validate', 'report'].indexOf(step);
  const selectClass =
    'h-10 w-full appearance-none rounded-xl border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="max-w-3xl p-6 sm:p-8"
    >
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        Import VIP contacts from CSV
      </h2>

      <div className="mt-3 flex items-center gap-2">
        {['Upload', 'Map columns', 'Validate', 'Report'].map((label, i) => (
          <React.Fragment key={label}>
            {i > 0 && (
              <span
                className="h-px w-6 bg-gray-300 dark:bg-gray-700"
                aria-hidden
              />
            )}
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                i === stepIndex
                  ? 'bg-brand-500 text-white'
                  : i < stepIndex
                    ? 'bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
                    : 'bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-gray-400'
              }`}
            >
              {i + 1}. {label}
            </span>
          </React.Fragment>
        ))}
      </div>

      <div className="mt-6">
        {step === 'upload' && (
          <div className="space-y-4">
            <label
              className="flex min-h-[160px] cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 px-6 py-8 text-center transition-colors hover:border-brand-400 dark:border-gray-700"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFile(e.dataTransfer.files?.[0]);
              }}
            >
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Drop a CSV here, or click to choose a file
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Up to {MAX_ROWS.toLocaleString()} rows. Phones are normalized to
                +1 and 10 US/Canada digits.
              </span>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] ?? undefined)}
              />
            </label>
            <button
              type="button"
              onClick={downloadTemplate}
              className="text-sm font-medium text-brand-600 underline hover:text-brand-700 dark:text-brand-400"
            >
              Download CSV template
            </button>
            {uploadError && (
              <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600">
                {uploadError}
              </div>
            )}
          </div>
        )}

        {step === 'map' && parsed && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {parsed.fileName}: {parsed.rows.length.toLocaleString()} rows.
              Columns were auto-matched where possible.
            </p>
            <div className="max-h-[45vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-white/[0.03]">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      Field
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      CSV column
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      Sample value
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                  {TARGET_FIELDS.map((field) => (
                    <tr key={field.key}>
                      <td className="px-4 py-2 font-medium text-gray-800 dark:text-gray-100">
                        {field.label}
                        {field.required && (
                          <span className="text-error-500"> *</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <select
                          className={selectClass}
                          value={mapping[field.key] ?? ''}
                          onChange={(e) =>
                            setMapping((prev) => ({
                              ...prev,
                              [field.key]: e.target.value,
                            }))
                          }
                        >
                          <option value="">— not mapped —</option>
                          {parsed.headers.map((header) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-2 text-gray-500">
                        {mapping[field.key]
                          ? sampleValue(mapping[field.key]) || '—'
                          : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!phoneIsMapped && (
              <div className="rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3 text-sm text-warning-700">
                Map a column to Phone to continue.
              </div>
            )}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={reset}
                className="app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium"
              >
                Back
              </button>
              <button
                type="button"
                disabled={!phoneIsMapped}
                onClick={() => setStep('validate')}
                className={`rounded-2xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 ${
                  phoneIsMapped ? '' : 'cursor-not-allowed opacity-50'
                }`}
              >
                Continue to validation
              </button>
            </div>
          </div>
        )}

        {step === 'validate' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <label className="rounded-2xl border border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
                <input
                  type="radio"
                  className="mr-2"
                  checked={mode === 'replace'}
                  onChange={() => {
                    setMode('replace');
                    setConfirmReplace(false);
                  }}
                />
                Replace entire list
              </label>
              <label className="rounded-2xl border border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
                <input
                  type="radio"
                  className="mr-2"
                  checked={mode === 'merge'}
                  onChange={() => setMode('merge')}
                />
                Merge (update existing phones)
              </label>
            </div>
            {mode === 'replace' && (
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={confirmReplace}
                  onChange={(e) => setConfirmReplace(e.target.checked)}
                />
                I understand this will delete the current VIP list for this{' '}
                {scope === 'group' ? 'group' : 'store'}.
              </label>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-success-200 bg-success-50 px-4 py-3 text-center">
                <div className="text-2xl font-semibold text-success-700">
                  {okRows.length}
                </div>
                <div className="text-xs text-success-700">Ready to import</div>
              </div>
              <div className="rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3 text-center">
                <div className="text-2xl font-semibold text-warning-700">
                  {skippedRows.length}
                </div>
                <div className="text-xs text-warning-700">
                  In-file duplicates
                </div>
              </div>
              <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-center">
                <div className="text-2xl font-semibold text-error-600">
                  {invalidRows.length}
                </div>
                <div className="text-xs text-error-600">Invalid rows</div>
              </div>
            </div>
            {importError && (
              <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600">
                {importError}
              </div>
            )}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep('map')}
                className="app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium"
              >
                Back
              </button>
              <button
                type="button"
                disabled={
                  okRows.length === 0 ||
                  submitting ||
                  (mode === 'replace' && !confirmReplace)
                }
                onClick={handleImport}
                className={`rounded-2xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 ${
                  okRows.length === 0 ||
                  submitting ||
                  (mode === 'replace' && !confirmReplace)
                    ? 'cursor-not-allowed opacity-50'
                    : ''
                }`}
              >
                {submitting
                  ? 'Importing…'
                  : `${mode === 'replace' ? 'Replace with' : 'Merge'} ${okRows.length.toLocaleString()} contacts`}
              </button>
            </div>
          </div>
        )}

        {step === 'report' && report && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-success-200 bg-success-50 px-4 py-3 text-center">
                <div className="text-2xl font-semibold text-success-700">
                  {report.created}
                </div>
                <div className="text-xs text-success-700">Created</div>
              </div>
              <div className="rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3 text-center">
                <div className="text-2xl font-semibold text-warning-700">
                  {report.updated}
                </div>
                <div className="text-xs text-warning-700">Updated</div>
              </div>
              <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-center">
                <div className="text-2xl font-semibold text-error-600">
                  {(report.skipped?.length || 0) +
                    (report.invalid?.length || 0)}
                </div>
                <div className="text-xs text-error-600">Skipped / invalid</div>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-2xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default VipCsvImportModal;
